import React from 'react';
import { useTranslation } from 'next-i18next';
import useOnclickOutside from 'react-cool-onclickoutside';
import classnames from 'classnames';

import { Button, Icon } from '@components/ui';
import { KEY_CODES } from '@constants/keycodes';
import * as ProductHelpers from '@helpers/ProductHelpers';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { useConfig } from '@hooks/data/useConfig';

import styles from './ProductQuantityEdit.module.scss';

export const SIZES = {
  LARGE: 'large',
  MEDIUM: 'medium',
  SMALL: 'small'
};

export const VARIANTS = {
  PRIMARY: 'primary',
  SUBTLE: 'subtle',
};

const ICON_SIZES = {
  [SIZES.LARGE]: 24,
  [SIZES.MEDIUM]: 18,
  [SIZES.SMALL]: 16,
};

const ProductQuantityEdit = (props) => {
  const {
    className,
    editorClassName,
    inputWrapperClassName,
    inputClassName,
    btnClassName,
    increaseButtonClassName,
    decreaseButtonClassName,
    //
    variant = VARIANTS.PRIMARY,
    size = SIZES.MEDIUM,
    //
    expanded = true,
    canToggle = false,
    verbose = false,
    //
    product,
    quantity,
    //
    isMutationError,
    disabled,
    //
    onQuantityChange,
    onExpandedChange,
  } = props;

  const inputRef = React.useRef();
  const editorRef = React.useRef();

  const [editValue, setEditValue] = React.useState(0);
  const [isEditMode, setEditMode] = React.useState(expanded);
  const [isManualEditMode, setManualEditMode] = React.useState(false);

  const { t } = useTranslation();
  const { data: config } = useConfig();
  const { errors } = config || {};

  const {
    value: quantityTarget,
    reset,
    decrease,
    increase,
    update
  } = useDebouncedValue(onQuantityChange, { ms: 600, initialValue: quantity });

  useOnclickOutside(() => {
    changeEditMode(false);
    setManualEditMode(false);
  }, {
    disabled: !canToggle || !isEditMode,
    refs: [editorRef],
  });

  useOnclickOutside(() => {
    setManualEditMode(false);
  }, { disabled: !isManualEditMode, refs: [editorRef] });

  // Effects

  React.useEffect(() => {
    changeEditMode(expanded);
  }, [expanded]);

  React.useEffect(() => {
    if (isMutationError) {
      reset();
    }
  }, [isMutationError]);

  // Variables

  const [maxQuantity, errorMessage] = React.useMemo(() => ProductHelpers.getMaxAllowedQuantity(product, errors), [product, errors]);

  const text = verbose ? t('PRODUCT.QUANTITY_IN_CART', { quantity: quantityTarget }) : quantityTarget;
  const value = isManualEditMode ? editValue : text;

  const canIncreaseQuantity = quantityTarget < maxQuantity;
  const canSaveEditValue = editValue <= maxQuantity;

  // Classes

  const rootClasses = classnames(
    styles.root,
    { [styles[`size-${size}`]]: size },
    { [styles[`variant-${variant}`]]: variant },
    { [styles.isEditMode]: isEditMode },
    className,
  );

  const editorClasses = classnames(
    styles.editor,
    editorClassName
  );

  const decreaseBtnClasses = classnames(
    styles.btn,
    styles.decreaseBtn,
    btnClassName,
    decreaseButtonClassName
  );

  const increaseBtnClasses = classnames(
    styles.btn,
    styles.increaseBtn,
    btnClassName,
    increaseButtonClassName
  );

  const inputWrapperClasses = classnames(
    styles.inputWrapper,
    { [styles.hasError]: isManualEditMode && !canSaveEditValue },
    { [styles.autoWidth]: verbose },
    inputWrapperClassName
  );

  const inputClasses = classnames(
    styles.input,
    { [styles.edit]: isManualEditMode },
    { [styles.error]: isManualEditMode && !canSaveEditValue },
    inputClassName,
  );

  const quantityBtnClasses = classnames(
    styles.btn,
    styles.quantityBtn,
  );

  // Handlers

  const changeEditMode = (editMode) => {
    setEditMode(editMode);
    if (onExpandedChange) {
      onExpandedChange(editMode);
    }
  };

  const onEnableEditMode = () => {
    if (canToggle) {
      changeEditMode(true);
    }
  };

  const onEnableManualEditMode = () => {
    if (!isManualEditMode) {
      setEditValue(quantityTarget);
      setManualEditMode(true);
      inputRef.current.select();
    }
  };

  const onSaveEdit = () => {
    update(editValue || 0);
    setManualEditMode(false);
  };

  const onCancelEdit = () => {
    setManualEditMode(false);
  };

  const onUpdateEditValue = (e) => {
    let newValue = e?.target?.value?.replace(/[^\d+]/, '');
    if (newValue) {
      newValue = parseInt(newValue || 0, 10);
    }
    setEditValue(newValue);
  };

  const onInputKeyDown = (e) => {
    e.stopPropagation();
    if (e.keyCode === KEY_CODES.ENTER && canSaveEditValue) {
      inputRef?.current?.blur();
      onSaveEdit();
    } else if (e.keyCode === KEY_CODES.ESC) {
      inputRef?.current?.blur();
      onCancelEdit();
    } else if (e.keyCode === KEY_CODES.TAB) {
      onCancelEdit();
    }
  };

  const onDecreaseClick = () => {
    if (isManualEditMode) {
      onCancelEdit();
    } else {
      decrease();
    }
  };

  const onIncreaseClick = () => {
    if (isManualEditMode) {
      onSaveEdit();
    } else {
      increase();
    }
  };

  return (
    <div className={rootClasses}>
      <div ref={editorRef} className={editorClasses}>

        <Button
          className={classnames(decreaseBtnClasses, { [styles.cancelBtn]: isManualEditMode })}
          size="small"
          variant="subtle"
          iconSize={ICON_SIZES[size]}
          disabled={disabled || (isManualEditMode ? false : quantityTarget <= 0)}
          onClick={onDecreaseClick}
          data-prevent-manual-edit-close={isManualEditMode}
        >
          <AnimatedIcon name="x" size={size} isVisible={isManualEditMode} />
          <AnimatedIcon name="trash" size={size} fromBottom isVisible={!isManualEditMode && quantityTarget === 1} />
          <AnimatedIcon name="minus" size={size} fromBottom isVisible={!isManualEditMode && quantityTarget > 1} />
        </Button>

        <div className={inputWrapperClasses}>
          <input
            ref={inputRef}
            className={inputClasses}
            type="text"
            inputMode="numeric"
            regex="^[0-9]*$"
            value={value}
            maxLength={3}
            size={verbose ? text.length : null}
            disabled={disabled}
            onChange={onUpdateEditValue}
            onKeyDown={onInputKeyDown}
            onClick={onEnableManualEditMode}
            onFocus={onEnableManualEditMode}
          />
        </div>
        <Button
          className={classnames(increaseBtnClasses, { [styles.confirmBtn]: isManualEditMode })}
          size="small"
          variant={isManualEditMode ? 'primary' : 'subtle'}
          iconSize={ICON_SIZES[size]}
          title={canIncreaseQuantity ? null : errorMessage}
          disabled={disabled || (isManualEditMode ? !canSaveEditValue : !canIncreaseQuantity)}
          onClick={onIncreaseClick}
          data-prevent-manual-edit-close={isManualEditMode}
        >
          <AnimatedIcon name="check" size={size} delayed isVisible={isManualEditMode} />
          <AnimatedIcon name="plus" size={size} delayed fromBottom isVisible={!isManualEditMode} />
        </Button>
      </div>
      {
        !isEditMode && (
          <Button
            className={quantityBtnClasses}
            size="small"
            variant="primary"
            onClick={onEnableEditMode}
          >
            {quantity}
          </Button>
        )
      }
    </div>
  );
};

const AnimatedIcon = (props) => {
  const {
    name, size, fromBottom, delayed, isVisible
  } = props;

  const classes = classnames(
    styles.animatedIcon,
    { [styles.fromBottom]: fromBottom },
    { [styles.delayed]: delayed },
    { [styles.visible]: isVisible }
  );

  return (
    <Icon
      className={classes}
      name={name}
      size={ICON_SIZES[size]}
    />
  );
};

export default ProductQuantityEdit;
